<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import DiscountSystemTab from '@/components/DiscountSystemTab.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const activeTab = ref(props?.data?.items[0]?.id);
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        :class="[
          data?.background_color
            ? `discount-system discount-system--${data.background_color}`
            : 'discount-system',
        ]"
      >
        <h2
          v-if="data.title"
          class="mb-0 discount-system__title h3"
          :class="[data?.background_color === 'black' ? 'white' : 'black']"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h2>
        <Tabs
          v-model:value="activeTab"
          class="discount-system__tabs"
          :scrollable="true"
        >
          <TabList
            ref="tabList"
            :class="`discount-system__tabs-headers tabs-bg-${data.background_color}`"
          >
            <Tab
              v-for="(tab, tabIndex) in data.items"
              :key="tab.id"
              :value="tab.id"
              class="discount-system__tabs-title"
              data-aos="fade-up"
              :data-aos-delay="tabIndex >= 1 ? 100 + 100 * tabIndex : 100"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="discount-system__tabs-content">
            <TabPanel v-for="tab in data.items" :key="tab.id" :value="tab.id">
              <DiscountSystemTab
                :data="tab"
                :background-color="data?.background_color"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.discount-system {
  $parent: &;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 16px;
  @include media-breakpoint-up(sm) {
    gap: 40px;
    padding: 48px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 40);
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(padding, 48);
  }
  &--white {
    background-color: $gray;
    #{$parent} {
      &__tabs-title {
        color: $on-surface-variant-2;
      }
    }
  }
  &--black {
    background-color: rgba($surface-container-opacity, 0.5);
    #{$parent} {
      &__tabs {
        &-title {
          color: $on-surface-variant-2;
          &[data-p='active'] {
            color: $white;
          }
        }
        &-headers {
          &:deep(.p-tablist-content) {
            border-bottom-color: $on-surface-variant-2;
          }
        }
      }
    }
  }
  &--gray {
    background-color: $white;
    #{$parent} {
      &__tabs-title {
        color: $on-surface-variant-2;
      }
    }
  }
  &__title {
    margin: 0;
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        gap: 8px;
        @include media-breakpoint-up(sm) {
          justify-content: center;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }

      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
    }
  }
}
</style>
